import React, { useEffect, useRef, useState } from "react";
import client from "../api/client";
import "./CdekWaybillModal.css";

// Накладная СДЭК: создание (кнопка «Отправить» в логистике) и правка или
// удаление (попап заказа). Данные и валидация — logistics/cdek/waybill.ts.

export interface WaybillForm {
  shipmentPoint: string;
  senderPhone: string;
  itemName: string;
  recipientName: string;
  recipientPhone: string;
  tariff: number;
  cityCode: number | null;
  cityLabel: string;
  address: string;
  deliveryPoint: string;
  amount: number;
  weightKg: number;
  length: number;
  width: number;
  height: number;
  cost: number;
  comment: string;
}

const TARIFFS = [
  { code: 482, label: "До двери" },
  { code: 483, label: "До ПВЗ" },
];

type Props = {
  userId: number;
  mode: "create" | "edit";
  onClose: () => void;
  /** create: накладная создана, номер уже записан в заказ. */
  onCreated?: (cdekNumber: string) => void;
  /** create: отправить без накладной. */
  onSkip?: () => void;
  /** edit: накладная удалена, status — статус заказа после удаления. */
  onDeleted?: (status: string) => void;
  onSaved?: () => void;
};

const errText = (e: any, fallback: string) => e?.response?.data?.error || e?.message || fallback;

const CdekWaybillModal: React.FC<Props> = ({ userId, mode, onClose, onCreated, onSkip, onDeleted, onSaved }) => {
  const [form, setForm] = useState<WaybillForm | null>(null);
  const [pointLabel, setPointLabel] = useState("");
  const [country, setCountry] = useState("");
  const [cdekNumber, setCdekNumber] = useState("");
  const [statusName, setStatusName] = useState("");
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<"" | "save" | "delete">("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Поиск города СДЭК.
  const [cityQuery, setCityQuery] = useState("");
  const [cityOptions, setCityOptions] = useState<{ code: number; label: string }[]>([]);
  const [cityOpen, setCityOpen] = useState(false);
  const cityTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const url = mode === "create" ? `/admin/cdek/prefill/${userId}` : `/admin/cdek/${userId}`;
    client
      .get(url)
      .then(({ data }) => {
        setForm(data.form);
        setPointLabel(data.shipmentPointLabel || data.form.shipmentPoint);
        setCountry(data.country || "");
        setCdekNumber(data.cdekNumber || "");
        setStatusName(data.statusName || "");
        setCityQuery(data.form.cityLabel || data.cityQuery || "");
      })
      .catch((e) => setLoadError(errText(e, "Не удалось загрузить накладную")));
  }, [userId, mode]);

  const set = <K extends keyof WaybillForm>(k: K, v: WaybillForm[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const searchCity = (q: string) => {
    setCityQuery(q);
    set("cityCode", null);
    window.clearTimeout(cityTimer.current);
    if (q.trim().length < 2) {
      setCityOptions([]);
      return;
    }
    cityTimer.current = window.setTimeout(() => {
      client
        .get("/admin/cdek/cities", { params: { name: q, country: country || undefined } })
        .then(({ data }) => {
          setCityOptions(Array.isArray(data) ? data : []);
          setCityOpen(true);
        })
        .catch(() => setCityOptions([]));
    }, 300);
  };

  const num = (v: string) => {
    const n = parseFloat(v.replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };

  const save = async () => {
    if (!form) return;
    setBusy("save");
    setError("");
    try {
      if (mode === "create") {
        const { data } = await client.post(`/admin/cdek/${userId}`, form);
        onCreated?.(data.cdekNumber || "");
      } else {
        await client.patch(`/admin/cdek/${userId}`, form);
        onSaved?.();
        onClose();
      }
    } catch (e) {
      setError(errText(e, "СДЭК не принял накладную"));
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    setBusy("delete");
    setError("");
    try {
      const { data } = await client.delete(`/admin/cdek/${userId}`);
      onDeleted?.(data.status);
      onClose();
    } catch (e) {
      setError(errText(e, "Не удалось удалить накладную"));
      setConfirmDelete(false);
    } finally {
      setBusy("");
    }
  };

  const volumetric = form ? (form.length * form.width * form.height) / 5000 : 0;
  const isPvz = form?.tariff === 483;

  return (
    <div className="cdek-overlay" onClick={busy ? undefined : onClose}>
      <div className="cdek-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cdek-head">
          <h3 className="cdek-title">
            {mode === "create" ? "Накладная СДЭК" : `Накладная ${cdekNumber || "СДЭК"}`}
          </h3>
          <button className="cdek-close" onClick={onClose} disabled={!!busy} aria-label="Закрыть">
            ×
          </button>
        </div>

        {loadError ? (
          <div className="cdek-body">
            <div className="cdek-error">{loadError}</div>
          </div>
        ) : !form ? (
          <div className="cdek-body cdek-muted">Загрузка…</div>
        ) : (
          <div className="cdek-body">
            {mode === "edit" && statusName && <div className="cdek-muted">Статус в СДЭК: {statusName}</div>}

            <label className="cdek-field">
              <span>Адрес отправки</span>
              <input className="cdek-input" value={pointLabel} disabled />
            </label>
            <label className="cdek-field">
              <span>Номер отправителя</span>
              <input
                className="cdek-input"
                value={form.senderPhone}
                disabled={mode === "edit"}
                onChange={(e) => set("senderPhone", e.target.value)}
              />
            </label>
            <label className="cdek-field">
              <span>Название</span>
              <input className="cdek-input" value={form.itemName} onChange={(e) => set("itemName", e.target.value)} />
            </label>
            <label className="cdek-field">
              <span>Получатель</span>
              <input
                className="cdek-input"
                value={form.recipientName}
                onChange={(e) => set("recipientName", e.target.value)}
              />
            </label>
            <label className="cdek-field">
              <span>Телефон получателя</span>
              <input
                className="cdek-input"
                value={form.recipientPhone}
                onChange={(e) => set("recipientPhone", e.target.value)}
              />
            </label>

            <div className="cdek-field">
              <span>Доставка</span>
              <div className="cdek-seg">
                {TARIFFS.map((t) => (
                  <button
                    key={t.code}
                    type="button"
                    className={`cdek-seg-btn${form.tariff === t.code ? " cdek-seg-btn--on" : ""}`}
                    onClick={() => set("tariff", t.code)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {isPvz ? (
              <label className="cdek-field">
                <span>Код ПВЗ получателя</span>
                <input
                  className="cdek-input"
                  placeholder="например, MSK123"
                  value={form.deliveryPoint}
                  onChange={(e) => set("deliveryPoint", e.target.value.toUpperCase())}
                />
              </label>
            ) : (
              <>
                <div className="cdek-field cdek-city">
                  <span>Город СДЭК</span>
                  <input
                    className={`cdek-input${form.cityCode ? "" : " cdek-input--warn"}`}
                    value={cityQuery}
                    placeholder="начните вводить город"
                    onChange={(e) => searchCity(e.target.value)}
                    onFocus={() => cityOptions.length && setCityOpen(true)}
                    onBlur={() => window.setTimeout(() => setCityOpen(false), 150)}
                  />
                  {!form.cityCode && <small className="cdek-hint">Выберите город из списка</small>}
                  {cityOpen && cityOptions.length > 0 && (
                    <div className="cdek-city-list">
                      {cityOptions.map((c) => (
                        <button
                          key={c.code}
                          type="button"
                          className="cdek-city-item"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            set("cityCode", c.code);
                            set("cityLabel", c.label);
                            setCityQuery(c.label);
                            setCityOpen(false);
                          }}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <label className="cdek-field">
                  <span>Адрес получателя</span>
                  <textarea
                    className="cdek-input"
                    rows={2}
                    value={form.address}
                    onChange={(e) => set("address", e.target.value)}
                  />
                </label>
              </>
            )}

            <div className="cdek-row">
              <label className="cdek-field">
                <span>Количество</span>
                <input
                  className="cdek-input"
                  inputMode="numeric"
                  value={form.amount}
                  onChange={(e) => set("amount", Math.max(0, Math.round(num(e.target.value))))}
                />
              </label>
              <label className="cdek-field">
                <span>Вес, кг</span>
                <input
                  className="cdek-input"
                  inputMode="decimal"
                  defaultValue={String(form.weightKg)}
                  onChange={(e) => set("weightKg", num(e.target.value))}
                />
              </label>
              <label className="cdek-field">
                <span>Оценка, ₸</span>
                <input
                  className="cdek-input"
                  inputMode="numeric"
                  value={form.cost}
                  onChange={(e) => set("cost", Math.round(num(e.target.value)))}
                />
              </label>
            </div>
            <div className="cdek-field">
              <span>Габариты, см (Д × Ш × В) · объёмный вес {volumetric.toFixed(3).replace(/\.?0+$/, "")} кг</span>
              <div className="cdek-row">
                {(["length", "width", "height"] as const).map((k) => (
                  <input
                    key={k}
                    className="cdek-input"
                    inputMode="numeric"
                    value={form[k]}
                    onChange={(e) => set(k, Math.round(num(e.target.value)))}
                  />
                ))}
              </div>
            </div>
            <label className="cdek-field">
              <span>Комментарий для СДЭК (номер СНТ)</span>
              <textarea
                className="cdek-input"
                rows={2}
                placeholder="KZ-SNT-…"
                value={form.comment}
                onChange={(e) => set("comment", e.target.value)}
              />
            </label>

            {error && <div className="cdek-error">{error}</div>}
          </div>
        )}

        {!loadError && form && (
          <div className="cdek-actions">
            {mode === "create" ? (
              <button className="cdek-btn-secondary" disabled={!!busy} onClick={onSkip}>
                Пропустить
              </button>
            ) : confirmDelete ? (
              <>
                <button className="cdek-btn-secondary" disabled={!!busy} onClick={() => setConfirmDelete(false)}>
                  Отмена
                </button>
                <button className="cdek-btn-danger" disabled={!!busy} onClick={remove}>
                  {busy === "delete" ? "Удаление…" : "Точно удалить"}
                </button>
              </>
            ) : (
              <button className="cdek-btn-danger-ghost" disabled={!!busy} onClick={() => setConfirmDelete(true)}>
                Удалить накладную
              </button>
            )}
            {!confirmDelete && (
              <button className="cdek-btn-primary" disabled={!!busy} onClick={save}>
                {busy === "save"
                  ? "Ждём СДЭК…"
                  : mode === "create"
                    ? "Создать и отправить"
                    : "Сохранить"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CdekWaybillModal;
